import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  computeLabelDiff,
  useUpdateLabelsForLabelableMutation,
} from '@github-ui/item-picker/commitUpdateLabelsForLabelableMutation'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {LabelPicker, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import {labelAccessibleName} from '@github-ui/label-token/RepositoryLabelToken'
import {type Label, LabelsList as Labels} from '@github-ui/labels-list'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useMemo, useRef, useState} from 'react'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {
  IssueNode,
  IssueNodeRepository,
  IssueViewerPageLabelEdge,
  IssueViewerPageLabelNode,
  IssueViewerPageLabelsConnection,
} from '../issue-viewer-page-query-types'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'

export const ReadonlyLabelsSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.labels} />

type LabelsSectionProps = {
  sectionHeader: React.ReactElement
  labels: Label[]
  onRemoveLabel?: (label: Label) => void
  removingLabelIds?: ReadonlySet<string>
  removeLabelFocusFallbackRef?: React.RefObject<HTMLButtonElement | null>
}
export const LabelsSection = ({
  sectionHeader,
  labels,
  onRemoveLabel,
  removingLabelIds,
  removeLabelFocusFallbackRef,
}: LabelsSectionProps) => {
  return (
    <Section
      id={`sidebar-labels-section`}
      sectionHeader={sectionHeader}
      emptyText={labels.length > 0 ? undefined : LABELS.emptySections.labels}
    >
      <Labels
        labels={labels}
        testId={TEST_IDS.issueLabels}
        onRemoveLabel={onRemoveLabel}
        removingLabelIds={removingLabelIds}
        removeLabelFocusFallbackRef={removeLabelFocusFallbackRef}
      />
    </Section>
  )
}

type LabelsSectionIssueRepository = Pick<IssueNodeRepository, 'name' | 'isArchived'> & {
  readonly owner: Pick<NonNullable<IssueNodeRepository['owner']>, 'login'>
}

type LabelsSectionLabelNode = Pick<IssueViewerPageLabelNode, keyof Label>
type LabelsSectionLabelEdge = Omit<IssueViewerPageLabelEdge, 'node'> & {
  readonly node: LabelsSectionLabelNode | null
}
type LabelsSectionLabelsConnection = Omit<IssueViewerPageLabelsConnection, 'edges'> & {
  readonly edges: ReadonlyArray<LabelsSectionLabelEdge | null> | null
}
type LabelsSectionLabelsFragment = {
  readonly labels: LabelsSectionLabelsConnection | null
}

export type EditIssueLabelsSectionFragment = Pick<IssueNode, 'id' | 'viewerCanLabel'> &
  LabelsSectionLabelsFragment & {
    readonly repository: LabelsSectionIssueRepository
  }

// RELAY-CLEANUP(LabelsSectionAssignedLabels): widens nullable fields so still-Relay parent data is
// assignable. Remove when the remaining Relay parents leave Relay.
export type LabelsSectionAssignedLabelsFragment = RelayCompat<LabelsSectionLabelsFragment>

export type EditIssueLabelsSectionProps = {
  onIssueUpdate?: () => void
  issue: EditIssueLabelsSectionFragment
  canCreateLabel?: boolean
  insideSidePanel?: boolean
  readonly: boolean
}

export function EditIssueLabelsSection({
  issue: data,
  canCreateLabel = false,
  onIssueUpdate,
  insideSidePanel,
  readonly,
}: EditIssueLabelsSectionProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const {
    repository: {
      owner: {login: owner},
      name: repo,
      isArchived: isRepositoryArchived,
    },
  } = data

  const {id: issueId, viewerCanLabel} = data

  const labels = useMemo(() => data.labels?.edges?.flatMap(e => (e?.node ? [e?.node] : [])) || [], [data.labels])

  const updateLabelsForLabelable = useUpdateLabelsForLabelableMutation({})
  const readonlyLabel = readonly || !viewerCanLabel || isRepositoryArchived
  const oneClickRemoveLabelsEnabled = isFeatureEnabled('issue_viewer_one_click_remove_labels')
  const [removingLabelIds, setRemovingLabelIds] = useState<ReadonlySet<string>>(() => new Set())
  const removingLabelIdsRef = useRef(new Set<string>())
  const labelPickerAnchorRef = useRef<HTMLButtonElement>(null)

  const {addToast} = useToastContext()

  const onSelectionChanged = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      const onErrorHandler = () => {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotUpdateLabels,
        })
      }

      // Use diff-based mutation to avoid race conditions with concurrent edits
      const {labelsToAdd, labelsToRemove} = computeLabelDiff(labels, selectedLabels)

      // If no changes, skip the mutation
      if (labelsToAdd.length === 0 && labelsToRemove.length === 0) {
        return
      }

      // Derive optimistic final labels from current store labels (which may include
      // concurrent additions from other users) by applying the diff, rather than
      // using selectedLabels which only reflects this user's picker state.
      const removeIds = new Set(labelsToRemove.map(l => l.id))
      const currentWithoutRemoved = labels.filter(l => !removeIds.has(l.id))
      const existingIds = new Set(currentWithoutRemoved.map(l => l.id))
      const optimisticLabels = [...currentWithoutRemoved, ...labelsToAdd.filter(l => !existingIds.has(l.id))]

      updateLabelsForLabelable({
        input: {
          labelableId: issueId,
          labelsToAdd,
          labelsToRemove,
          labelableTypeName: 'Issue',
        },
        expectedFinalLabels: optimisticLabels,
        onError: onErrorHandler,
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, issueId, labels, onIssueUpdate, updateLabelsForLabelable],
  )

  const onRemoveLabel = useCallback(
    (label: Label) => {
      if (removingLabelIdsRef.current.has(label.id)) return

      removingLabelIdsRef.current.add(label.id)
      setRemovingLabelIds(ids => new Set(ids).add(label.id))
      announce(`Removing label: ${labelAccessibleName(label)}.`)

      const clearPendingRemoval = () => {
        removingLabelIdsRef.current.delete(label.id)
        setRemovingLabelIds(ids => {
          const nextIds = new Set(ids)
          nextIds.delete(label.id)
          return nextIds
        })
      }
      const onErrorHandler = () => {
        clearPendingRemoval()
        announce(`Could not remove label: ${labelAccessibleName(label)}. The label was restored.`, {assertive: true})
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotUpdateLabels,
        })
      }

      updateLabelsForLabelable({
        input: {
          labelableId: issueId,
          labelsToAdd: [],
          labelsToRemove: [label],
          labelableTypeName: 'Issue',
        },
        expectedFinalLabels: labels.filter(currentLabel => currentLabel.id !== label.id),
        onError: onErrorHandler,
        onCompleted: () => {
          clearPendingRemoval()
          announce(`Removed label: ${labelAccessibleName(label)}.`)
          onIssueUpdate?.()
        },
      })
    },
    [addToast, issueId, labels, onIssueUpdate, updateLabelsForLabelable],
  )

  const labelSectionHeader = useMemo(() => {
    if (readonlyLabel) {
      return <ReadonlyLabelsSectionHeader />
    }

    return (
      <LabelPicker
        repo={repo}
        owner={owner}
        readonly={readonlyLabel}
        labels={labels}
        canCreateLabel={canCreateLabel}
        enableSuggestedLabels
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader
            title={LABELS.sectionTitles.labels}
            buttonProps={{
              ...anchorProps,
              onClick: e => {
                sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABELS_SECTION_EDIT_CLICK')
                anchorProps.onClick?.(e)
              },
            }}
            ref={node => {
              labelPickerAnchorRef.current = node
              if (typeof ref === 'function') (ref as (element: HTMLButtonElement | null) => void)(node)
              else if (ref) ref.current = node
            }}
          />
        )}
        insidePortal={insideSidePanel}
        showNoMatchItem
      />
    )
  }, [readonlyLabel, owner, repo, labels, canCreateLabel, onSelectionChanged, insideSidePanel, sendAnalyticsEvent])

  return (
    <LabelsSection
      sectionHeader={labelSectionHeader}
      labels={labels}
      onRemoveLabel={oneClickRemoveLabelsEnabled && !readonlyLabel ? onRemoveLabel : undefined}
      removingLabelIds={removingLabelIds}
      removeLabelFocusFallbackRef={labelPickerAnchorRef}
    />
  )
}
